import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FEATURE_NAVIGATION } from '../constants/featureNavigation';
import './CommandPalette.css';

/**
 * Quick links to the pages where common tasks are performed. These NAVIGATE \u2014
 * they don't fire the action from the palette (the labels used to say "Send
 * Shock" / "Take Screenshot" but only opened a page, which was misleading).
 * Kept as a curated shortlist alongside the full feature index.
 */
const QUICK_ACTIONS = [
    { id: 'action:shock', label: 'PiShock', desc: 'Open the shock controls', category: 'action', icon: '\u26A1', route: '/app/integrations/pishock' },
    { id: 'action:spin', label: 'Spin Wheel', desc: 'Open the spin wheel', category: 'action', icon: '\uD83C\uDFA1', route: '/app/punishment/spin-wheel' },
    { id: 'action:chat', label: 'Chat', desc: 'Open chat', category: 'action', icon: '\uD83D\uDCAC', route: '/app/communication/chat' },
    { id: 'action:interrogate', label: 'Interrogation', desc: 'Open the interrogation console', category: 'action', icon: '\u2753', route: '/app/communication/interrogation' },
    { id: 'action:block', label: 'Time Limits', desc: 'Open limits & app blocks', category: 'action', icon: '\uD83D\uDEAB', route: '/app/monitoring/time-limit' },
    { id: 'action:write-lines', label: 'Write Lines', desc: 'Open the write-lines page', category: 'action', icon: '\u270D\uFE0F', route: '/app/punishment/write-lines' },
    { id: 'action:challenge', label: 'Challenges', desc: 'Open challenges', category: 'action', icon: '\uD83C\uDFAF', route: '/app/games/challenges' },
    { id: 'action:sabotage', label: 'Input Sabotage', desc: 'Open input sabotage', category: 'action', icon: '\uD83D\uDDB1\uFE0F', route: '/app/punishment/input-sabotage' },
    { id: 'action:dossier', label: 'Dossier', desc: 'Open the behavioral dossier', category: 'action', icon: '\uD83D\uDD0D', route: '/app/monitoring/dossier' },
    { id: 'action:timeline', label: 'Sacred Timeline', desc: 'Open the activity timeline', category: 'action', icon: '\uD83D\uDCF8', route: '/app/monitoring/sacred-timeline' },
];

/**
 * Build searchable items from feature navigation + quick actions
 */
function buildSearchItems() {
    const items = [];

    // Navigation items from feature config
    Object.values(FEATURE_NAVIGATION).forEach(category => {
        Object.values(category.types).forEach(type => {
            items.push({
                id: `nav:${type.id}`,
                label: type.label,
                desc: category.label,
                category: category.id,
                icon: type.icon,
                route: type.to,
                kind: 'navigate',
            });
        });
    });

    // Quick actions
    QUICK_ACTIONS.forEach(action => {
        items.push({
            ...action,
            kind: 'action',
        });
    });

    return items;
}

/**
 * Simple fuzzy match: checks if all query chars appear in order in the target
 * Returns { match: boolean, highlighted: React node }
 */
function fuzzyMatch(query, text) {
    if (!query) return { match: true, highlighted: text };

    const lower = text.toLowerCase();
    const qLower = query.toLowerCase();
    let qi = 0;
    const parts = [];
    let lastIdx = 0;

    for (let i = 0; i < lower.length && qi < qLower.length; i++) {
        if (lower[i] === qLower[qi]) {
            if (i > lastIdx) {
                parts.push({ text: text.slice(lastIdx, i), match: false });
            }
            parts.push({ text: text[i], match: true });
            lastIdx = i + 1;
            qi++;
        }
    }

    if (qi < qLower.length) return { match: false, highlighted: text };

    if (lastIdx < text.length) {
        parts.push({ text: text.slice(lastIdx), match: false });
    }

    const highlighted = parts.map((p, i) =>
        p.match ? <mark key={i}>{p.text}</mark> : <span key={i}>{p.text}</span>
    );

    return { match: true, highlighted };
}

const CommandPalette = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);

    const allItems = useMemo(() => buildSearchItems(), []);

    // Filter items by query
    const results = useMemo(() => {
        if (!query.trim()) return allItems;

        return allItems
            .map(item => {
                const labelMatch = fuzzyMatch(query, item.label);
                const descMatch = fuzzyMatch(query, item.desc);
                return {
                    ...item,
                    _labelMatch: labelMatch,
                    _descMatch: descMatch,
                    _matched: labelMatch.match || descMatch.match,
                    // Score: exact start > label match > desc match
                    _score: item.label.toLowerCase().startsWith(query.toLowerCase()) ? 2
                        : labelMatch.match ? 1 : 0,
                };
            })
            .filter(r => r._matched)
            .sort((a, b) => b._score - a._score);
    }, [query, allItems]);

    // Group results by kind
    const grouped = useMemo(() => {
        const navItems = results.filter(r => r.kind === 'navigate');
        const actionItems = results.filter(r => r.kind === 'action');
        const groups = [];
        if (navItems.length > 0) groups.push({ label: 'Navigate', items: navItems });
        if (actionItems.length > 0) groups.push({ label: 'Jump to', items: actionItems });
        return groups;
    }, [results]);

    // Flat list for keyboard navigation
    const flatResults = useMemo(() =>
        grouped.flatMap(g => g.items),
        [grouped]
    );

    // Reset on open
    useEffect(() => {
        if (isOpen) {
            setQuery('');
            setActiveIndex(0);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [isOpen]);

    // Clamp active index
    useEffect(() => {
        if (activeIndex >= flatResults.length) {
            setActiveIndex(Math.max(0, flatResults.length - 1));
        }
    }, [flatResults.length, activeIndex]);

    // Scroll active item into view
    useEffect(() => {
        const el = listRef.current?.querySelector('.cmd-palette-item.is-active');
        el?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);

    const selectItem = useCallback((item) => {
        if (item.route) {
            navigate(item.route);
        }
        onClose();
    }, [navigate, onClose]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(i => Math.min(i + 1, flatResults.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (flatResults[activeIndex]) {
                selectItem(flatResults[activeIndex]);
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
        }
    }, [flatResults, activeIndex, selectItem, onClose]);

    if (!isOpen) return null;

    let itemCounter = 0;

    return (
        <div className="cmd-palette-backdrop" onClick={onClose}>
            <div
                className="cmd-palette"
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Command palette"
            >
                {/* Search Input */}
                <div className="cmd-palette-input-wrap">
                    <span className="cmd-search-icon" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/>
                        </svg>
                    </span>
                    <input
                        ref={inputRef}
                        className="cmd-palette-input"
                        type="text"
                        placeholder="Search views, actions..."
                        value={query}
                        onChange={e => { setQuery(e.target.value); setActiveIndex(0); }}
                        onKeyDown={handleKeyDown}
                        spellCheck={false}
                        autoComplete="off"
                        role="combobox"
                        aria-expanded="true"
                        aria-controls="cmd-palette-listbox"
                        aria-activedescendant={flatResults.length > 0 ? `cmd-palette-opt-${activeIndex}` : undefined}
                        aria-autocomplete="list"
                        aria-label="Search views and actions"
                    />
                    <span className="cmd-palette-kbd">ESC</span>
                </div>

                {/* Results */}
                <div className="cmd-palette-results" ref={listRef} role="listbox" id="cmd-palette-listbox" aria-label="Results">
                    {flatResults.length === 0 ? (
                        <div className="cmd-palette-empty">
                            <div className="cmd-palette-empty-icon">?</div>
                            <div className="cmd-palette-empty-text">
                                No results for "{query}"
                            </div>
                        </div>
                    ) : (
                        grouped.map(group => (
                            <div key={group.label}>
                                <div className="cmd-palette-group-label">{group.label}</div>
                                {group.items.map(item => {
                                    const idx = itemCounter++;
                                    const isActive = idx === activeIndex;
                                    const labelContent = item._labelMatch?.highlighted || item.label;
                                    const isCurrentPage = location.pathname === item.route;

                                    return (
                                        <div
                                            key={item.id}
                                            id={`cmd-palette-opt-${idx}`}
                                            role="option"
                                            aria-selected={isActive}
                                            className={`cmd-palette-item ${isActive ? 'is-active' : ''}`}
                                            onClick={() => selectItem(item)}
                                            onMouseEnter={() => setActiveIndex(idx)}
                                        >
                                            <div className={`cmd-palette-item-icon cat-${item.category}`}>
                                                {item.kind === 'action' ? (
                                                    <span>{item.icon}</span>
                                                ) : (
                                                    <i className={item.icon} style={{ fontSize: 14, color: '#fff' }} />
                                                )}
                                            </div>
                                            <div className="cmd-palette-item-text">
                                                <div className="cmd-palette-item-label">{labelContent}</div>
                                                <div className="cmd-palette-item-desc">{item.desc}</div>
                                            </div>
                                            {isCurrentPage && (
                                                <span className="cmd-palette-item-badge">Current</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))
                    )}
                </div>

                {/* Footer Hints */}
                <div className="cmd-palette-footer">
                    <div className="cmd-palette-footer-hint">
                        <kbd>&uarr;</kbd><kbd>&darr;</kbd> navigate
                    </div>
                    <div className="cmd-palette-footer-hint">
                        <kbd>&crarr;</kbd> select
                    </div>
                    <div className="cmd-palette-footer-hint">
                        <kbd>esc</kbd> close
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CommandPalette;
